/**
 * Pricing tier data for the in-app paywall modal. Mirrors the tiers defined
 * on the marketing site's /pricing page
 * (see `zoey_marketing/src/pages/pricing.astro`). Kept in sync by hand — if
 * pricing changes on the marketing site, update this file too.
 */

import type { SubscriptionTier } from "@/lib/models";

export type Cadence = "monthly" | "annual";

export interface PricingTier {
  id: Extract<
    SubscriptionTier,
    "zoey_starter" | "zoey_plus" | "zoey_family"
  >;
  name: string;
  monthlyPrice: string; // "$4.99"
  annualPrice: string; // "$39.99"
  annualHelper: string; // "save 33%"
  description: string;
  badge?: string;
  highlight?: boolean;
  features: string[];
}

export const PRICING_TIERS: readonly PricingTier[] = [
  {
    id: "zoey_starter",
    name: "Zoey Starter",
    monthlyPrice: "$4.99",
    annualPrice: "$39.99",
    annualHelper: "save 33%",
    description:
      "Perfect for couples and small households. All the core tools plus a taste of AI.",
    features: [
      "Posts, chats & family wall",
      "Chores & task assignment",
      "Shopping & grocery lists",
      "Calendar & events (manual + iCal sync)",
      "Responsibilities & duty tracking",
      "Up to 4 household members",
      "AI meal suggestions (dinner only)",
    ],
  },
  {
    id: "zoey_plus",
    name: "Zoey+",
    monthlyPrice: "$9.99",
    annualPrice: "$89.99",
    annualHelper: "save 25%",
    description:
      "Full AI-powered planning for the whole family — meals, weekends, groceries, and more.",
    badge: "Most popular",
    highlight: true,
    features: [
      "Everything in Starter, plus:",
      "Up to 8 household members",
      "AI meal suggestions (breakfast, lunch & dinner)",
      "Packed lunch ideas",
      "Weekend activity plans (AI + weather)",
      "Receipt scanning for groceries",
      "Buy-again intelligence",
    ],
  },
  {
    id: "zoey_family",
    name: "Zoey Family",
    monthlyPrice: "$14.99",
    annualPrice: "$129.99",
    annualHelper: "save 28%",
    description:
      "Everything in Zoey+, plus tools for blended families, co-parents, and extended households.",
    features: [
      "Everything in Zoey+, plus:",
      "Co-parenting coordination",
      "Guest passes for caregivers & babysitters",
      "Post reach & extended family sharing",
      "Priority support",
    ],
  },
] as const;
